import axios from "axios";
import { useEffect, useState } from "react";
import ApiUrl from "../Environment/ApiUrl";
import { useNavigate } from "react-router-dom";
import { useToast } from "../Toast/ToastContext";

function AddLoan({ toggleSidebar }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [showModal, setShowModal] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [generateNumber, setGenerateNumber] = useState("");
  const [customerData, setCustomerData] = useState([]);
  const [productData, setProductData] = useState([]);
  const [actionType, setActionType] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [isManualProductInput, setIsManualProductInput] = useState(false);
  const [code, setCode] = useState("");
  const [inputNumber, setInputNumber] = useState("");
  const [amount, setAmount] = useState("");
  const [userdata, setUserdata] = useState([]);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [selectedManager, setSelectedManager] = useState("");
  const [selectedStaff, setSelectedStaff] = useState("");
  const [staffList, setStaffList] = useState([]);
  const [isManagerLocked, setIsManagerLocked] = useState(false);
  const [isStaffLocked, setIsStaffLocked] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [productFormErrors, setProductFormErrors] = useState({});
  const [existingLoanIds, setExistingLoanIds] = useState([]);
  const [existingCustomerGroups, setExistingCustomerGroups] = useState({});
  const [customerNameTamil, setCustomerNameTamil] = useState("");
  const [generatedLoanId, setGeneratedLoanId] = useState("");
  const [editingItemId, setEditingItemId] = useState(null);
  const [usedInputNumbers, setUsedInputNumbers] = useState({});

  const [productAddData, setProductAddData] = useState({
    item_code: "",
    item_name: "",
    item_name_english: "",
    item_name_tamil: "",
    category: "",
    type: "",
    pure_percent: "",
    status: "Active",
    image: null,
  });

  // ── Get Current Date ─────────────────────────────────────────────────────
  const getCurrentDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // ── State Definitions ────────────────────────────────────────────────────
  const [addData, setAddData] = useState({
    loan_id: "",
    loan_date: getCurrentDate(),
    customer_type: "",
    customer_id: "",
    customer_name: "",
    customer_name_tamil: "",
    mobile: "",
    address: "",
    loan_amount: "",
    notes: "",
    loan_list: [],
    created_by: "system",
    created_date: "",
    manager: "",
    manager_name: "",
    staff: "",
  });

  const [addCustomerData, setAddCustomerData] = useState({
    customer_type: "",
    customer_id: "",
    name: "",
    name_tamil: "",
    mobile: "",
    address: "",
    status: "",
    created_by: "system",
    created_date: "",
    updated_by: "",
    updated_date: "",
  });

  const [newItem, setNewItem] = useState({
    loan_id: "",
    product_name: "",
    product_name_tamil: "",
    category: "",
    quantity: "1",
    weight: "",
    amount: "",
  });

  // ── Translation Function ────────────────────────────────────────────────
  const translateToTamil = async (englishText) => {
    if (!englishText || englishText.trim().length < 2) return "";

    setIsTranslating(true);

    const apis = [
      async () => {
        const encoded = encodeURIComponent(englishText.trim());
        const res = await fetch(
          `https://api.mymemory.translated.net/get?q=${encoded}&langpair=en|ta`,
        );
        const data = await res.json();
        const translated = data?.responseData?.translatedText;
        if (
          translated &&
          translated.toLowerCase() !== englishText.toLowerCase()
        ) {
          return translated;
        }
        return null;
      },
      async () => {
        const encoded = encodeURIComponent(englishText.trim());
        const res = await fetch(`https://lingva.ml/api/v1/en/ta/${encoded}`);
        if (res.ok) {
          const data = await res.json();
          if (data.translation && data.translation !== englishText) {
            return data.translation;
          }
        }
        return null;
      },
      async () => {
        const res = await fetch("https://libretranslate.com/translate", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            q: englishText.trim(),
            source: "en",
            target: "ta",
            format: "text",
          }),
        });
        if (res.ok) {
          const data = await res.json();
          if (data.translatedText) {
            return data.translatedText;
          }
        }
        return null;
      },
    ];

    for (const api of apis) {
      try {
        const result = await api();
        if (result) {
          setIsTranslating(false);
          return result;
        }
      } catch (error) {
        console.warn("Translation API failed:", error);
        continue;
      }
    }

    setIsTranslating(false);
    return "";
  };

  // Only one code is ever "open" at a time — the first letter that isn't full
  const getActiveCode = () => {
    for (let i = 0; i < 26; i++) {
      const letter = String.fromCharCode(65 + i);
      const usedCount = usedInputNumbers[letter]?.length || 0;
      if (usedCount < 500) return letter;
    }
    return null; // all 26 codes are full
  };
  // ── Get Next Available Input Number for a Code ─────────────────────────
  const getNextAvailableInputNumber = (codeLetter) => {
    // Find all existing loan IDs that start with L{codeLetter}
    const existingIds = existingLoanIds.filter((id) =>
      id.startsWith(`L${codeLetter}`),
    );

    if (existingIds.length === 0) {
      return 1; // If no existing loans for this code, start with 1
    }

    // Extract the input numbers from existing IDs
    const numbers = existingIds
      .map((id) => {
        const match = id.match(/^L([A-Z])(\d+)-/);
        return match ? parseInt(match[2]) : 0;
      })
      .filter((num) => num > 0);

    if (numbers.length === 0) return 1;

    // Sort numbers and find the first gap
    numbers.sort((a, b) => a - b);
    let nextNumber = 1;
    for (let i = 0; i < numbers.length; i++) {
      if (numbers[i] > nextNumber) break;
      if (numbers[i] === nextNumber) {
        nextNumber++;
      }
    }
    return nextNumber;
  };

  // ── Generate Unique Loan ID ─────────────────────────────────────────────
  const generateUniqueLoanId = async () => {
    if (!code || !inputNumber || !amount || parseFloat(amount) <= 0) {
      setGeneratedLoanId("");
      return;
    }

    const baseId = `L${code}${inputNumber}`;
    const amountRounded = Math.round(parseFloat(amount));

    // Check if this exact combination exists
    const exactId = `${baseId}-${amountRounded}`.toUpperCase();

    if (!existingLoanIds.includes(exactId)) {
      setGeneratedLoanId(exactId);
      setAddData((prev) => ({ ...prev, loan_id: exactId }));
      return exactId;
    }

    // If exists, find next available number for this series
    let newInputNumber = parseInt(inputNumber) + 1;
    while (newInputNumber <= 500) {
      const newId = `L${code}${newInputNumber}-${amountRounded}`.toUpperCase();
      if (!existingLoanIds.includes(newId)) {
        setInputNumber(newInputNumber.toString());
        setGeneratedLoanId(newId);
        setAddData((prev) => ({ ...prev, loan_id: newId }));
        toast.info(
          `Loan ID ${exactId} already exists. Using ${newId} instead.`,
          3000,
          "topRight",
        );
        return newId;
      }
      newInputNumber++;
    }

    toast.error(
      `No available Loan ID for series ${code}${inputNumber}. Please try a different code.`,
      5000,
      "topRight",
    );
    return null;
  };

  // ── Get Next Available Number for Customer Series ──────────────────────
  const getNextAvailableNumber = (series) => {
    const group = existingCustomerGroups[series];
    if (!group || !group.customers || group.customers.length === 0) {
      return 1;
    }

    const numbers = group.customers
      .map((id) => {
        const match = id.match(/^([A-Z]+)(\d+)/);
        return match ? parseInt(match[2]) : 0;
      })
      .filter((num) => num > 0);

    if (numbers.length === 0) return 1;

    numbers.sort((a, b) => a - b);
    let nextNumber = 1;
    for (let i = 0; i < numbers.length; i++) {
      if (numbers[i] > nextNumber) break;
      if (numbers[i] === nextNumber) {
        nextNumber++;
      }
    }
    return nextNumber;
  };

  useEffect(() => {
    const activeCode = getActiveCode();
    if (activeCode) {
      setCode(activeCode);
      setInputNumber(getNextAvailableInputNumber(activeCode).toString());
    }
  }, [usedInputNumbers]);

  // ── Auto-generate Loan ID when code, inputNumber, or amount changes ────
  useEffect(() => {
    const generateId = async () => {
      if (code && inputNumber && amount && parseFloat(amount) > 0) {
        await generateUniqueLoanId();
      } else {
        setGeneratedLoanId("");
        setAddData((prev) => ({ ...prev, loan_id: "" }));
      }
    };
    generateId();
  }, [code, inputNumber, amount, existingLoanIds]);

  // ── Auto-fetch customer details when series changes ─────────────────────
  useEffect(() => {
    if (
      !code ||
      !inputNumber ||
      !addData.customer_type ||
      addData.customer_type === "Walk-In"
    )
      return;

    const series = `${code}${inputNumber}`;
    const group = existingCustomerGroups[series];

    if (group && group.customers && group.customers.length > 0) {
      const existingCustomerId = group.customers[0];
      const customerName = group.names[existingCustomerId] || "";
      const customerTamilName = group.tamilNames[existingCustomerId] || "";

      setAddData((prev) => ({
        ...prev,
        customer_id: existingCustomerId,
        customer_name: customerName,
        customer_name_tamil: customerTamilName,
      }));

      setCustomerNameTamil(customerTamilName);

      const customer = customerData.find(
        (c) => c.customer_id === existingCustomerId,
      );
      if (customer) {
        setAddData((prev) => ({
          ...prev,
          mobile: customer.mobile || "",
          address: customer.address || "",
        }));
      }

      toast.info(
        `Customer found: ${customerName}${customerTamilName ? ` / ${customerTamilName}` : ""}`,
        3000,
        "topRight",
      );
    } else {
      const nextNumber = getNextAvailableNumber(series);
      const newCustomerId = `${series}${nextNumber.toString().padStart(3, "0")}`;

      setAddData((prev) => ({
        ...prev,
        customer_id: newCustomerId,
        customer_name: "",
        customer_name_tamil: "",
        mobile: "",
        address: "",
      }));

      setCustomerNameTamil("");
    }
  }, [
    code,
    inputNumber,
    addData.customer_type,
    existingCustomerGroups,
    customerData,
  ]);

  // ── Open/Close Modals ────────────────────────────────────────────────────
  const openModal = () => setShowModal(true);
  const closeModal = () => setShowModal(false);

  const openCustomerModal = () => {
    setAddCustomerData({
      ...addCustomerData,
      customer_type: addData.customer_type,
    });
    generateCustomerId(addData.customer_type);
    setShowCustomerModal(true);
  };

  const closeCustomerModal = () => {
    setShowCustomerModal(false);
    setActionType("");
    setAddCustomerData({
      customer_type: "",
      customer_id: "",
      name: "",
      name_tamil: "",
      mobile: "",
      address: "",
      status: "",
      created_by: "system",
      created_date: "",
      updated_by: "",
      updated_date: "",
    });
    setValidationErrors({});
  };

  // ── Fetch existing data ──────────────────────────────────────────────────
  const fetchExistingData = async () => {
    try {
      const response = await axios.get(`${ApiUrl}/Api/loan.php`);
      const loans = response.data?.data || [];

      const ids = loans.map((loan) => loan.loan_id).filter(Boolean);
      setExistingLoanIds(ids);

      // Build used input numbers per code
      const usedNumbers = {};
      ids.forEach((id) => {
        const match = id.match(/^L([A-Z])(\d+)-/);
        if (match) {
          const codeLetter = match[1];
          const number = parseInt(match[2]);
          if (!usedNumbers[codeLetter]) {
            usedNumbers[codeLetter] = [];
          }
          usedNumbers[codeLetter].push(number);
        }
      });
      setUsedInputNumbers(usedNumbers);

      const groups = {};
      loans.forEach((loan) => {
        if (loan.customer_id) {
          const match = loan.customer_id.match(/^([A-Z]+)(\d+)/);
          if (match) {
            const series = match[1];
            if (!groups[series]) {
              groups[series] = {
                customers: [],
                names: {},
                tamilNames: {},
              };
            }
            if (!groups[series].customers.includes(loan.customer_id)) {
              groups[series].customers.push(loan.customer_id);
              groups[series].names[loan.customer_id] = loan.customer_name || "";
              groups[series].tamilNames[loan.customer_id] =
                loan.customer_name_tamil || "";
            }
          }
        }
      });
      setExistingCustomerGroups(groups);
    } catch (error) {
      console.error("Error fetching existing loan data:", error);
    }
  };

  // ── Fetch initial data ──────────────────────────────────────────────────
  useEffect(() => {
    toggleSidebar();
    fetchDataGenerate();
    fetchCustomerData();
    fetchProductData();
    fetchUserData();
    fetchLoggedInUser();
    fetchExistingData();
  }, []);

  const fetchDataGenerate = async () => {
    try {
      const response = await axios.get(
        `${ApiUrl}/Api/loan.php/generate-loan-code`,
      );
      setGenerateNumber(response.data.loan_id || "");
    } catch (error) {
      console.error("Error fetching loan code:", error.message);
      setGenerateNumber("");
      toast.error("Failed to generate loan ID", 3000, "topRight");
    }
  };

  const fetchCustomerData = async () => {
    try {
      const response = await axios.get(`${ApiUrl}/Api/customer.php`);
      const data = response.data?.data || [];
      setCustomerData(data.reverse());
    } catch (error) {
      console.error("Error fetching customer data:", error.message);
      setCustomerData([]);
      toast.error("Failed to fetch customer data", 3000, "topRight");
    }
  };

  const fetchProductData = async () => {
    try {
      const response = await axios.get(`${ApiUrl}/Api/product.php`);
      setProductData(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching product data:", error.message);
      setProductData([]);
      toast.error("Failed to fetch product data", 3000, "topRight");
    }
  };

  const fetchUserData = async () => {
    try {
      const res = await axios.get(`${ApiUrl}/Api/user.php`);
      const users = Array.isArray(res.data.data) ? res.data.data : [];
      setUserdata(users);
    } catch (err) {
      console.error("Failed to fetch users", err);
      toast.error("Failed to load staff data", 3000, "topRight");
    }
  };

  const fetchLoggedInUser = () => {
    try {
      const userInfo = JSON.parse(localStorage.getItem("user") || "{}");
      if (userInfo && userInfo.user_id) {
        setLoggedInUser(userInfo);
      }
    } catch (error) {
      console.error("Error reading logged-in user:", error);
    }
  };

  // ── Product Modal Functions ─────────────────────────────────────────────
  const fetchProductItemCode = async () => {
    try {
      const response = await axios.get(
        `${ApiUrl}/Api/product.php/generate-item-code`,
      );
      setProductAddData((prev) => ({
        ...prev,
        item_code: response.data?.item_code || "",
      }));
    } catch (error) {
      console.error("Error fetching product item code:", error.message);
      toast.error("Failed to generate product code", 3000, "topRight");
    }
  };

  const openProductModal = () => {
    fetchProductItemCode();
    setProductAddData({
      item_code: "",
      item_name: "",
      item_name_english: "",
      item_name_tamil: "",
      category: "",
      type: "",
      pure_percent: "",
      status: "Active",
      image: null,
    });
    setProductFormErrors({});
    setShowProductModal(true);
  };

  const closeProductModal = () => {
    setShowProductModal(false);
    setProductAddData({
      item_code: "",
      item_name: "",
      item_name_english: "",
      item_name_tamil: "",
      category: "",
      type: "",
      pure_percent: "",
      status: "Active",
      image: null,
    });
    setProductFormErrors({});
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();

    const errors = {};
    if (!productAddData.item_name_english)
      errors.item_name = "Item name is required";
    if (!productAddData.category) errors.category = "Category is required";
    if (!productAddData.pure_percent)
      errors.pure_percent = "Purity percentage is required";
    if (!productAddData.status) errors.status = "Status is required";

    if (Object.keys(errors).length > 0) {
      setProductFormErrors(errors);
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();

      let finalTamilName = productAddData.item_name_tamil;
      if (!finalTamilName && productAddData.item_name_english) {
        finalTamilName = await translateToTamil(
          productAddData.item_name_english,
        );
      }

      formData.append("item_code", productAddData.item_code || "");
      formData.append("item_name", productAddData.item_name_english || "");
      formData.append(
        "item_name_english",
        productAddData.item_name_english || "",
      );
      formData.append("item_name_tamil", finalTamilName || "");
      formData.append("category", productAddData.category || "");
      formData.append("type", productAddData.type || "");
      formData.append("pure_percent", productAddData.pure_percent || "");
      formData.append("status", productAddData.status || "");
      formData.append("created_by", "system");

      if (productAddData.image) {
        formData.append("image", productAddData.image);
      }

      const response = await axios.post(`${ApiUrl}/Api/product.php`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (
        response.data?.status === true ||
        response.status === 200 ||
        response.status === 201
      ) {
        toast.success("Product added successfully!", 3000, "topRight");
        closeProductModal();
        await fetchProductData();
      } else {
        throw new Error(response.data?.message || "Failed to add product");
      }
    } catch (error) {
      console.error("Error adding product:", error);
      toast.error(
        error.response?.data?.message || "Failed to add product",
        3000,
        "topRight",
      );
    } finally {
      setLoading(false);
    }
  };

  // ── Customer ID Generation ──────────────────────────────────────────────
  const generateCustomerId = async (customerType) => {
    if (customerType === "Walk-In") {
      setAddCustomerData((prev) => ({
        ...prev,
        customer_id: "TJCWI-001",
      }));
    } else if (customerType === "Retailer" || customerType === "Regular") {
      const series = code || "L";
      const existingInGroup = customerData.filter(
        (c) =>
          c.customer_type === customerType &&
          c.customer_id &&
          c.customer_id.startsWith(series),
      );

      const numericPart = String(existingInGroup.length + 1).padStart(3, "0");
      const newCustomerId = `${series}${numericPart}`;

      setAddCustomerData((prev) => ({
        ...prev,
        customer_id: newCustomerId,
      }));
    } else {
      try {
        const response = await axios.get(
          `${ApiUrl}/Api/customer.php/generate-customer-code`,
        );
        setAddCustomerData((prev) => ({
          ...prev,
          customer_id: response.data?.customer_id || "",
        }));
      } catch (error) {
        console.error("Error fetching generated ID:", error.message);
        setAddCustomerData((prev) => ({ ...prev, customer_id: "" }));
        toast.error("Failed to generate customer ID", 3000, "topRight");
      }
    }
  };

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleCustomerTypeChange = (e) => {
    const customerType = e.target.value;
    setAddData({
      ...addData,
      customer_type: customerType,
      customer_id: "",
      customer_name: "",
      customer_name_tamil: "",
      mobile: "",
      address: "",
    });
    setAddCustomerData({ ...addCustomerData, customer_type: customerType });
    generateCustomerId(customerType);
  };

  const handleCustomerChange = (e) => {
    const selectedCustomerId = e.target.value;
    const selectedCustomer = customerData.find(
      (customer) =>
        `${customer.customer_id}-${customer.id}` === selectedCustomerId,
    );

    if (selectedCustomer) {
      setAddData({
        ...addData,
        customer_id: selectedCustomer.customer_id,
        customer_name: selectedCustomer.name || "",
        customer_name_tamil: selectedCustomer.name_tamil || "",
        mobile: selectedCustomer.mobile || "",
        address: selectedCustomer.address || "",
      });
      setCustomerNameTamil(selectedCustomer.name_tamil || "");
    } else {
      setAddData({
        ...addData,
        customer_id: "",
        customer_name: "",
        customer_name_tamil: "",
        mobile: "",
        address: "",
      });
      setCustomerNameTamil("");
    }
  };

  const handleItemInputChange = (e) => {
    const { name, value } = e.target;
    setNewItem({
      ...newItem,
      [name]: value,
    });
  };

  const calculateTotalWorth = (items) => {
    const total = items.reduce((sum, item) => {
      const amount = parseFloat(item.amount) || 0;
      return sum + amount;
    }, 0);
    return total.toFixed(2);
  };

  useEffect(() => {
    const totalWorth = calculateTotalWorth(addData.loan_list);
    setAddData((prevData) => ({
      ...prevData,
      loan_amount: totalWorth,
    }));
  }, [addData.loan_list]);

  // ── Add/Edit/Remove Item ────────────────────────────────────────────────
  const handleAddItem = () => {
    if (!newItem.category) {
      toast.error("Please select a category", 3000, "topRight");
      return;
    }
    if (!newItem.product_name) {
      toast.error("Please enter/select a product name", 3000, "topRight");
      return;
    }
    if (!newItem.quantity || parseFloat(newItem.quantity) <= 0) {
      toast.error("Please enter a valid quantity", 3000, "topRight");
      return;
    }
    if (!newItem.weight || parseFloat(newItem.weight) <= 0) {
      toast.error("Please enter a valid weight", 3000, "topRight");
      return;
    }
    if (!newItem.amount || parseFloat(newItem.amount) <= 0) {
      toast.error("Please enter a valid amount", 3000, "topRight");
      return;
    }

    const selectedProduct = productData.find(
      (p) => (p.item_name_english || p.item_name) === newItem.product_name,
    );

    const itemToAdd = {
      ...newItem,
      product_name_tamil:
        selectedProduct?.item_name_tamil || newItem.product_name_tamil || "",
      id: Date.now().toString(),
      loan_id: addData.loan_id,
    };

    const updatedItems = [...addData.loan_list, itemToAdd];

    setAddData({
      ...addData,
      loan_list: updatedItems,
    });

    setNewItem({
      loan_id: addData.loan_id,
      product_name: "",
      product_name_tamil: "",
      category: "",
      quantity: "1",
      weight: "",
      amount: "",
    });

    setIsManualProductInput(false);
  };

  const handleEditItem = (item) => {
    setEditingItemId(item.id);
    setNewItem({
      loan_id: item.loan_id || addData.loan_id,
      product_name: item.product_name,
      product_name_tamil: item.product_name_tamil || "",
      category: item.category,
      quantity: item.quantity,
      weight: item.weight,
      amount: item.amount,
    });
    const isInList = productData.some(
      (product) => product.item_name === item.product_name,
    );
    setIsManualProductInput(!isInList);
  };

  const handleUpdateItem = () => {
    if (editingItemId === null) return;

    const selectedProduct = productData.find(
      (p) => (p.item_name_english || p.item_name) === newItem.product_name,
    );

    const updatedItems = addData.loan_list.map((item) => {
      if (item.id === editingItemId) {
        return {
          ...item,
          loan_id: addData.loan_id,
          product_name: newItem.product_name,
          product_name_tamil:
            selectedProduct?.item_name_tamil ||
            newItem.product_name_tamil ||
            "",
          category: newItem.category,
          quantity: newItem.quantity,
          weight: newItem.weight,
          amount: newItem.amount,
        };
      }
      return item;
    });

    setAddData({
      ...addData,
      loan_list: updatedItems,
    });

    setEditingItemId(null);
    setNewItem({
      loan_id: addData.loan_id,
      product_name: "",
      product_name_tamil: "",
      category: "",
      quantity: "1",
      weight: "",
      amount: "",
    });
    setIsManualProductInput(false);
  };

  const handleRemoveItem = (id) => {
    const updatedItems = addData.loan_list.filter((item) => item.id !== id);
    setAddData({
      ...addData,
      loan_list: updatedItems,
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAddData({
      ...addData,
      [name]: value,
    });
  };

  useEffect(() => {
    const total = calculateTotalWorth(addData.loan_list);
    setAmount(total);
  }, [addData.loan_list]);

  // ── Customer Submit ──────────────────────────────────────────────────────
  const validateCustomerForm = (data) => {
    const errors = {};
    if (!data.customer_type) errors.customer_type = "Customer type is required";

    const role = JSON.parse(localStorage.getItem("user") || "{}").role || "";
    const isAdmin = role === "Admin" || role === "admin";

    if (!selectedManager) {
      toast.error("Please select a Manager", 3000, "topRight");
      return false;
    }
    if (!isAdmin && !selectedStaff) {
      toast.error("Please select a Staff", 3000, "topRight");
      return false;
    }
    if (!data.name) errors.name = "Name is required";
    if (!data.mobile || !/^\d{10}$/.test(data.mobile))
      errors.mobile = "Valid 10-digit mobile number is required";
    if (!data.status) errors.status = "Status is required";
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCustomerSubmit = async (e) => {
    e.preventDefault();

    if (!validateCustomerForm(addCustomerData)) return;

    try {
      setLoading(true);

      let tamilName = addCustomerData.name_tamil;
      if (!tamilName && addCustomerData.name) {
        tamilName = await translateToTamil(addCustomerData.name);
        if (tamilName) {
          toast.success(`Tamil translation: ${tamilName}`, 2000, "topRight");
        }
      }

      const formData = {
        ...addCustomerData,
        name_tamil: tamilName || "",
        aadhar_number: addCustomerData.aadhar_number || "",
        created_date: new Date().toISOString().slice(0, 19).replace("T", " "),
        updated_date: new Date().toISOString().slice(0, 19).replace("T", " "),
        created_by_manager: selectedManager,
        created_by_staff: selectedStaff,
      };

      await axios.post(`${ApiUrl}/Api/customer.php`, formData);

      toast.success("Customer added successfully!", 3000, "topRight");

      await fetchCustomerData();

      setAddData((prevData) => ({
        ...prevData,
        customer_type: addCustomerData.customer_type,
        customer_id: addCustomerData.customer_id,
        customer_name: addCustomerData.name,
        customer_name_tamil: tamilName || "",
        mobile: addCustomerData.mobile,
        address: addCustomerData.address,
        status: addCustomerData.status,
        created_by: "AddLoan",
        created_date: new Date().toISOString(),
      }));

      setCustomerNameTamil(tamilName || "");

      closeCustomerModal();
    } catch (error) {
      console.error("Error adding customer:", error.message);
      toast.error("Failed to add customer", 3000, "topRight");
    } finally {
      setLoading(false);
    }
  };

  // ── Loan Submit ──────────────────────────────────────────────────────────
  const handleLoanSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      if (!addData.customer_type) {
        toast.error("Please select a customer type", 3000, "topRight");
        return;
      }
      if (!addData.customer_id) {
        toast.error("Please select a customer", 3000, "topRight");
        return;
      }
      if (addData.loan_list.length === 0) {
        toast.error("Please add at least one item", 3000, "topRight");
        return;
      }
      if (!addData.loan_amount || parseFloat(addData.loan_amount) <= 0) {
        toast.error("Please ensure a valid loan amount", 3000, "topRight");
        return;
      }
      if (!addData.loan_id) {
        toast.error(
          "Please select Code and Input to generate Loan ID",
          3000,
          "topRight",
        );
        return;
      }

      const role = JSON.parse(localStorage.getItem("user") || "{}").role || "";
      const isAdmin = role === "Admin" || role === "admin";

      if (!selectedManager) {
        toast.error("Please select a Manager", 3000, "topRight");
        return;
      }
      if (!isAdmin && !selectedStaff) {
        toast.error("Please select a Staff", 3000, "topRight");
        return;
      }

      if (existingLoanIds.includes(addData.loan_id)) {
        toast.error(
          `Loan ID ${addData.loan_id} already exists! Please use a different combination.`,
          5000,
          "topRight",
        );
        return;
      }

      const formData = {
        loan_id: addData.loan_id,
        loan_date: addData.loan_date,
        customer_id: addData.customer_id,
        customer_name: addData.customer_name,
        customer_name_tamil: addData.customer_name_tamil || "",
        customer_type: addData.customer_type,
        loan_amount: addData.loan_amount,
        notes: addData.notes,
        loan_list: addData.loan_list.map((item) => ({
          ...item,
          product_name_tamil: item.product_name_tamil || "",
        })),
        manager: addData.manager || "",
        manager_name: addData.manager_name || "",
        staff: addData.staff || "",
        created_by: addData.created_by,
        created_date: new Date().toISOString().slice(0, 19).replace("T", " "),
      };

      const response = await axios.post(`${ApiUrl}/Api/loan.php`, formData);

      if (response.data.status) {
        toast.success("Loan added successfully!", 3000, "topRight");
        navigate("/loan");
      } else {
        toast.error(
          response.data.message || "Failed to save loan data",
          3000,
          "topRight",
        );
      }
    } catch (error) {
      console.error("Error creating loan:", error);
      toast.error("Error creating loan. Please try again.", 3000, "topRight");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/loan");
  };

  // ── Manager/Staff Handlers ──────────────────────────────────────────────
  useEffect(() => {
    if (!loggedInUser || userdata.length === 0) return;

    const currentUser = userdata.find(
      (u) => u.user_id === loggedInUser.user_id,
    );
    if (!currentUser) return;

    if (currentUser.role === "Manager" || currentUser.role === "manager") {
      setSelectedManager(currentUser.id.toString());
      setIsManagerLocked(true);

      const staff = userdata.filter(
        (u) =>
          u.manager_id === currentUser.user_id &&
          (u.role === "Employee" || u.role === "employee"),
      );
      setStaffList(staff);

      setAddData((prev) => ({
        ...prev,
        manager: currentUser.user_id,
        manager_name: currentUser.name,
      }));
    } else if (
      currentUser.role === "Employee" ||
      currentUser.role === "employee"
    ) {
      setSelectedStaff(currentUser.name);
      setIsStaffLocked(true);

      const manager = userdata.find(
        (u) =>
          u.user_id === currentUser.manager_id &&
          (u.role === "Manager" || u.role === "manager"),
      );

      if (manager) {
        setSelectedManager(manager.id.toString());
        setIsManagerLocked(true);

        const staffUnderManager = userdata.filter(
          (u) =>
            u.manager_id === manager.user_id &&
            (u.role === "Employee" || u.role === "employee"),
        );
        setStaffList(staffUnderManager);

        setAddData((prev) => ({
          ...prev,
          manager: manager.user_id,
          manager_name: manager.name,
          staff: currentUser.name,
        }));
      }
    }
  }, [loggedInUser, userdata]);

  const handleManagerChange = (e) => {
    if (isManagerLocked) return;
    const managerId = e.target.value;
    setSelectedManager(managerId);
    setSelectedStaff("");
    setStaffList([]);

    if (managerId) {
      const managerObj = userdata.find((u) => u.id === parseInt(managerId));
      if (managerObj) {
        const staff = userdata.filter(
          (u) =>
            u.manager_id === managerObj.user_id &&
            (u.role === "Employee" || u.role === "employee"),
        );
        setStaffList(staff);

        setAddData((prev) => ({
          ...prev,
          manager: managerObj.user_id,
          manager_name: managerObj.name,
        }));
      }
    } else {
      setAddData((prev) => ({ ...prev, manager: "", manager_name: "" }));
    }
  };

  const handleStaffChange = (e) => {
    if (isStaffLocked) return;
    const staffName = e.target.value;
    setSelectedStaff(staffName);
    setAddData((prev) => ({ ...prev, staff: staffName }));
  };

  // ── Filtered Data ────────────────────────────────────────────────────────
  const filteredProducts = productData.filter(
    (product) => product.category === newItem.category,
  );

  const filteredCustomers = addData.customer_type
    ? customerData.filter(
        (customer) => customer.customer_type === addData.customer_type,
      )
    : [];

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <>
      {loading && (
        <div className="loading-overlay">
          <div className="loading-message">
            <div className="loadinggif"></div>
          </div>
        </div>
      )}

      <div className="container-fluid position-relative py-5 mb-4">
        <div
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            zIndex: 1000,
          }}
        >
          <button className="btn btn-secondary btn-sm" onClick={handleCancel}>
            <i className="fa-solid fa-arrow-left me-1"></i> Back
          </button>
        </div>
        <div className="row g-3">
          <div className="col-lg-5">
            <div className="card p-2">
              <div className="row g-2">
                <div className="col-md-3">
                  <label className="form-label">Loan ID</label>
                  <input
                    type="text"
                    className={`form-control ${existingLoanIds.includes(addData.loan_id) ? "is-invalid" : generatedLoanId ? "bg-success text-white" : "bg-light"}`}
                    value={addData.loan_id || "Select Code, Input & Amount"}
                    disabled
                    readOnly
                  />
                  {existingLoanIds.includes(addData.loan_id) && (
                    <div className="invalid-feedback">
                      This Loan ID already exists!
                    </div>
                  )}
                  {generatedLoanId &&
                    !existingLoanIds.includes(generatedLoanId) && (
                      <small className="text-success d-block mt-1">
                        <i className="bi bi-check-circle"></i> Loan ID generated
                        successfully
                      </small>
                    )}
                </div>
                <div className="col-md-3">
                  <label className="form-label">Loan Date</label>
                  <input
                    type="date"
                    className="form-control"
                    name="loan_date"
                    value={addData.loan_date}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="col-lg-3 col-md-3">
                  <div className="mb-3">
                    <label className="form-label">Code (A-Z)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={code || "Calculating..."}
                      readOnly
                      disabled
                      style={{ backgroundColor: "#f8f9fa", color: "#6c757d" }}
                    />
                  </div>
                </div>
                <div className="col-lg-3 col-md-3">
                  <div className="mb-3">
                    <label className="form-label">Input (1-500)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={inputNumber || ""}
                      readOnly
                      disabled
                      style={{ backgroundColor: "#f8f9fa", color: "#6c757d" }}
                    />
                    {/* {code && (
      <small className="text-muted d-block mt-1">
        Used: {usedInputNumbers[code]?.length || 0}/500
      </small>
    )} */}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-7">
            <div className="card h-100 p-2">
              <div className="row g-2">
                <div className="col-md-3">
                  <label className="form-label">
                    Customer Type <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    name="customer_type"
                    value={addData.customer_type}
                    onChange={handleCustomerTypeChange}
                  >
                    <option value="">Select Type</option>
                    <option value="Walk-In">Walk-In</option>
                    <option value="Retailer">Retailer</option>
                    <option value="Regular">Regular</option>
                  </select>
                </div>
                <div className="col-sm-6 col-md-3">
                  <label className="form-label">
                    Customer <span className="text-danger">*</span>
                  </label>
                  <div className="input-group">
                    <select
                      className="form-select"
                      onChange={handleCustomerChange}
                      value={
                        addData.customer_id
                          ? `${addData.customer_id}-${
                              customerData.find(
                                (c) =>
                                  c.customer_id === addData.customer_id &&
                                  c.name === addData.customer_name,
                              )?.id || ""
                            }`
                          : ""
                      }
                      disabled={!addData.customer_type}
                    >
                      <option value="">Select Customer</option>
                      {Array.isArray(filteredCustomers) &&
                        filteredCustomers.map((item) => {
                          const displayName = item.name_tamil
                            ? `${item.name} / ${item.name_tamil}`
                            : item.name;
                          return (
                            <option
                              key={`${item.customer_id}-${item.id}`}
                              value={`${item.customer_id}-${item.id}`}
                            >
                              {item.customer_id} - {displayName}
                            </option>
                          );
                        })}
                    </select>
                    <div className="btn-group">
                      <button
                        type="button"
                        className="btn btn-outline-dark btn-sm mx-1"
                        onClick={openCustomerModal}
                        disabled={!addData.customer_type}
                      >
                        <i className="bi bi-plus"></i>
                      </button>
                    </div>
                  </div>
                  {addData.customer_id && (
                    <small className="text-success">
                      <i className="bi bi-check-circle"></i> Customer selected
                    </small>
                  )}
                </div>
                <div className="col-sm-6 col-md-3">
                  <label className="form-label">Name</label>
                  <input
                    type="text"
                    className="form-control"
                    name="customer_name"
                    placeholder="Name"
                    value={addData.customer_name || ""}
                    onChange={handleInputChange}
                    disabled
                  />
                  {customerNameTamil && (
                    <small className="text-muted d-block">
                      <span className="tamil-text">{customerNameTamil}</span>
                    </small>
                  )}
                </div>
                <div className="col-sm-6 col-md-3">
                  <label className="form-label">Mobile Number</label>
                  <input
                    type="text"
                    className="form-control"
                    name="mobile"
                    value={addData.mobile || ""}
                    placeholder="Mobile Number"
                    onChange={handleInputChange}
                    disabled
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row mt-3">
          {(() => {
            const role =
              JSON.parse(localStorage.getItem("user") || "{}").role || "";
            const isAdmin = role === "Admin" || role === "admin";
            return (
              <>
                <div className="col-md-3">
                  <label className="form-label">
                    Manager <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={selectedManager}
                    onChange={handleManagerChange}
                    disabled={isManagerLocked}
                  >
                    <option value="">Select Manager</option>
                    {userdata
                      .filter(
                        (u) => u.role === "Manager" || u.role === "manager",
                      )
                      .map((manager) => (
                        <option key={manager.id} value={manager.id}>
                          {manager.name}
                        </option>
                      ))}
                  </select>
                </div>

                {!isAdmin && (
                  <div className="col-md-3">
                    <label className="form-label">
                      Staff <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      value={selectedStaff}
                      onChange={handleStaffChange}
                      disabled={isStaffLocked || !selectedManager}
                    >
                      <option value="">Select Staff</option>
                      {staffList.map((staff) => (
                        <option key={staff.id} value={staff.name}>
                          {staff.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </>
            );
          })()}
        </div>

        {/* ── Item Input Section ── */}
        <div className="card card-body mt-2">
          <div className="row">
            <div className="col-md-2">
              <label className="form-label">Select Category</label>
              <select
                className="form-select"
                name="category"
                value={newItem.category}
                onChange={handleItemInputChange}
              >
                <option value="">Select Category</option>
                <option value="Gold">Gold</option>
                <option value="Silver">Silver</option>
                <option value="Gold Coin">Gold Coin</option>
                <option value="Silver Coin">Silver Coin</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Product Name</label>
              <div className="d-flex align-items-center">
                {isManualProductInput ? (
                  <input
                    type="text"
                    className="form-control me-2"
                    name="product_name"
                    value={newItem.product_name}
                    onChange={handleItemInputChange}
                    disabled={!newItem.category}
                    placeholder="Enter product name"
                  />
                ) : (
                  <>
                    <select
                      className="form-select me-2"
                      name="product_name"
                      value={newItem.product_name}
                      onChange={(e) => {
                        const selectedValue = e.target.value;
                        const selectedProduct = filteredProducts.find(
                          (p) =>
                            (p.item_name_english || p.item_name) ===
                            selectedValue,
                        );
                        setNewItem({
                          ...newItem,
                          product_name: selectedValue,
                          product_name_tamil:
                            selectedProduct?.item_name_tamil || "",
                          product_name_english:
                            selectedProduct?.item_name_english ||
                            selectedProduct?.item_name ||
                            selectedValue,
                        });
                      }}
                      disabled={!newItem.category}
                    >
                      <option value="">Select Product</option>
                      {filteredProducts.map((product) => {
                        const displayName = product.item_name_tamil
                          ? `${product.item_name_english || product.item_name} / ${product.item_name_tamil}`
                          : product.item_name_english || product.item_name;
                        return (
                          <option
                            key={product.id}
                            value={
                              product.item_name_english || product.item_name
                            }
                          >
                            {displayName}
                          </option>
                        );
                      })}
                    </select>
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm me-1"
                      onClick={openProductModal}
                      title="Add New Product"
                      disabled={!newItem.category}
                    >
                      <i className="bi bi-plus"></i>
                    </button>
                  </>
                )}
              </div>
              {!newItem.category && (
                <small className="text-muted text-danger">
                  Please select category first
                </small>
              )}
            </div>

            <div className="col-md-2">
              <label className="form-label">Qty</label>
              <input
                type="text"
                inputMode="numeric"
                className="form-control"
                placeholder="Qty"
                name="quantity"
                value={newItem.quantity}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*$/.test(value)) {
                    handleItemInputChange(e);
                  }
                }}
              />
            </div>

            <div className="col-md-2">
              <label className="form-label">Weight (g)</label>
              <input
                type="text"
                inputMode="decimal"
                className="form-control"
                placeholder="(g)"
                name="weight"
                value={newItem.weight}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*\.?\d{0,3}$/.test(value)) {
                    handleItemInputChange(e);
                  }
                }}
              />
            </div>

            <div className="col-md-2">
              <label className="form-label">Amount (₹)</label>
              <input
                type="text"
                inputMode="decimal"
                className="form-control"
                placeholder="Amount"
                name="amount"
                value={newItem.amount}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*\.?\d*$/.test(value)) {
                    handleItemInputChange(e);
                  }
                }}
              />
            </div>

            <div className="col-md-1">
              <label className="form-label"></label>
              {editingItemId === null ? (
                <button
                  className="btn btn-success btn-sm w-100 mt-4"
                  onClick={handleAddItem}
                >
                  <i className="bi bi-plus"></i>
                </button>
              ) : (
                <button
                  className="btn btn-info btn-sm w-100 mt-4"
                  onClick={handleUpdateItem}
                >
                  <i className="bi bi-check"></i>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ── Item List Table ── */}
        <div className="container-fluid mt-2">
          <div className="row">
            <div className="col-lg-8">
              <div className="row">
                <div className="card card-body">
                  <h6>Item List</h6>
                  <div className="p-3" style={{ backgroundColor: "#E8F0FA" }}>
                    <div className="table-container">
                      <table className="table table-layout-fixed w-100">
                        <thead className="bg-light">
                          <tr>
                            <th>S.No</th>
                            <th>Category</th>
                            <th>Product</th>
                            <th>Qty</th>
                            <th>Weight</th>
                            <th>Amount (₹)</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {addData.loan_list.length > 0 ? (
                            addData.loan_list.map((item, index) => {
                              const displayName = item.product_name_tamil
                                ? `${item.product_name} / ${item.product_name_tamil}`
                                : item.product_name;
                              return (
                                <tr key={item.id || index}>
                                  <td>{index + 1}</td>
                                  <td>{item.category}</td>
                                  <td>{displayName}</td>
                                  <td>{item.quantity}</td>
                                  <td>{item.weight}</td>
                                  <td>{item.amount}</td>
                                  <td>
                                    <button
                                      className="btn btn-sm btn-warning me-1"
                                      onClick={() => handleEditItem(item)}
                                    >
                                      <i className="bi bi-pencil"></i>
                                    </button>
                                    <button
                                      className="btn btn-sm btn-danger"
                                      onClick={() => handleRemoveItem(item.id)}
                                    >
                                      <i className="bi bi-trash"></i>
                                    </button>
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan="7" className="text-center">
                                No items added yet
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Right Side - Loan Amount & Submit ── */}
            <div className="col-lg-4">
              <div className="card card-body">
                <div className="row mb-2">
                  <div className="col-md-6">
                    <label className="form-label">Loan Amount (₹)</label>
                  </div>
                  <div className="col-md-6">
                    <input
                      type="text"
                      className="form-control"
                      name="loan_amount"
                      value={addData.loan_amount}
                      readOnly
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label">Notes</label>
                  </div>
                  <div className="col-md-6">
                    <input
                      type="text"
                      className="form-control"
                      name="notes"
                      value={addData.notes}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="row">
                  <div className="col-lg-12">
                    <button
                      className="btn btn-sm btn-success w-100"
                      onClick={handleLoanSubmit}
                      disabled={
                        isSubmitting ||
                        !addData.loan_id ||
                        existingLoanIds.includes(addData.loan_id)
                      }
                    >
                      {isSubmitting ? "Submitting..." : "Submit"}
                    </button>
                    {!addData.loan_id && (
                      <small className="text-warning d-block text-center mt-1">
                        ⚠️ Please select Code, Input, and add items to generate
                        Loan ID
                      </small>
                    )}
                    {addData.loan_id &&
                      existingLoanIds.includes(addData.loan_id) && (
                        <small className="text-danger d-block text-center mt-1">
                          ⚠️ This Loan ID already exists! Please change
                          Code/Input
                        </small>
                      )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Add Customer Modal ── */}
      <div
        className={`modal fade ${showCustomerModal ? "show" : ""}`}
        style={{
          display: showCustomerModal ? "block" : "none",
          backgroundColor: "rgba(0,0,0,0.5)",
        }}
        tabIndex="-1"
        aria-labelledby="addCustomerModalLabel"
        aria-hidden={!showCustomerModal}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h6
                className="modal-title text-primary"
                id="addCustomerModalLabel"
              >
                Add Customer
              </h6>
              <button
                type="button"
                className="btn-close"
                style={{ fontSize: "10px" }}
                onClick={closeCustomerModal}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleCustomerSubmit}>
                <div className="row">
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label htmlFor="customerType" className="form-label">
                        Customer Type <span className="text-danger">*</span>
                      </label>
                      <select
                        className={`form-control ${
                          validationErrors.customer_type ? "is-invalid" : ""
                        }`}
                        id="customerType"
                        value={addCustomerData.customer_type}
                        onChange={(e) => {
                          const customerType = e.target.value;
                          setAddCustomerData({
                            ...addCustomerData,
                            customer_type: customerType,
                          });
                          generateCustomerId(customerType);
                        }}
                        required
                        disabled={addData.customer_type}
                      >
                        <option value="" disabled>
                          Select Customer Type
                        </option>
                        <option value="Walk-In">Walk-In</option>
                        <option value="Regular">Regular</option>
                        <option value="Retailer">Retailer</option>
                      </select>
                      {validationErrors.customer_type && (
                        <div className="invalid-feedback">
                          {validationErrors.customer_type}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label htmlFor="customerId" className="form-label">
                        Customer ID
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="customerId"
                        placeholder="Customer ID will be generated"
                        value={addCustomerData.customer_id}
                        disabled
                      />
                      <small className="text-muted">
                        Based on selected Code: {code || "L"}
                      </small>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label htmlFor="name" className="form-label">
                        Name <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <input
                          type="text"
                          className={`form-control ${
                            validationErrors.name ? "is-invalid" : ""
                          }`}
                          id="name"
                          placeholder="Enter Name (Tamil will auto-translate)"
                          value={addCustomerData.name}
                          onChange={(e) =>
                            setAddCustomerData({
                              ...addCustomerData,
                              name: e.target.value,
                              name_tamil: "",
                            })
                          }
                          onBlur={async (e) => {
                            const val = e.target.value.trim();
                            if (val && !addCustomerData.name_tamil) {
                              const tamil = await translateToTamil(val);
                              if (tamil) {
                                setAddCustomerData((prev) => ({
                                  ...prev,
                                  name_tamil: tamil,
                                }));
                                toast.info(
                                  `Tamil translation: ${tamil}`,
                                  2000,
                                  "topRight",
                                );
                              }
                            }
                          }}
                          required
                        />
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          title="Translate to Tamil"
                          disabled={isTranslating || !addCustomerData.name}
                          onClick={async () => {
                            const tamil = await translateToTamil(
                              addCustomerData.name,
                            );
                            if (tamil) {
                              setAddCustomerData((prev) => ({
                                ...prev,
                                name_tamil: tamil,
                              }));
                              toast.success(
                                "Translated successfully!",
                                1500,
                                "topRight",
                              );
                            } else {
                              toast.error(
                                "Translation failed. Please try again.",
                                2000,
                                "topRight",
                              );
                            }
                          }}
                        >
                          {isTranslating ? (
                            <span className="spinner-border spinner-border-sm" />
                          ) : (
                            "🌐 தமிழ்"
                          )}
                        </button>
                      </div>
                      {addCustomerData.name_tamil && (
                        <small className="text-muted mt-1 d-block">
                          <strong>Tamil:</strong> {addCustomerData.name_tamil}
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 ms-2 text-muted"
                            onClick={() =>
                              setAddCustomerData((prev) => ({
                                ...prev,
                                name_tamil: "",
                              }))
                            }
                          >
                            ✕ clear
                          </button>
                        </small>
                      )}
                      {validationErrors.name && (
                        <div className="invalid-feedback">
                          {validationErrors.name}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label htmlFor="mobile" className="form-label">
                        Mobile Number <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength="10"
                        className={`form-control ${
                          validationErrors.mobile ? "is-invalid" : ""
                        }`}
                        id="mobile"
                        placeholder="Enter Mobile"
                        value={addCustomerData.mobile}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d{0,10}$/.test(value)) {
                            setAddCustomerData({
                              ...addCustomerData,
                              mobile: value,
                            });
                          }
                        }}
                        required
                      />
                      {validationErrors.mobile && (
                        <div className="invalid-feedback">
                          {validationErrors.mobile}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <label htmlFor="address" className="form-label">
                    Address
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="address"
                    placeholder="Enter Address"
                    value={addCustomerData.address}
                    onChange={(e) =>
                      setAddCustomerData({
                        ...addCustomerData,
                        address: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="customerStatus" className="form-label">
                    Status <span className="text-danger">*</span>
                  </label>
                  <select
                    className={`form-select ${
                      validationErrors.status ? "is-invalid" : ""
                    }`}
                    id="status"
                    value={addCustomerData.status}
                    onChange={(e) =>
                      setAddCustomerData({
                        ...addCustomerData,
                        status: e.target.value,
                      })
                    }
                    required
                  >
                    <option value="" disabled>
                      Select Status
                    </option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                  {validationErrors.status && (
                    <div className="invalid-feedback">
                      {validationErrors.status}
                    </div>
                  )}
                </div>

                <div className="text-center mt-4">
                  <button
                    type="submit"
                    className="btn btn-primary btn-sm px-4"
                    disabled={loading}
                  >
                    {loading ? "Submitting..." : "Submit"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* ── Add Product Modal ── */}
      <div
        className={`modal fade ${showProductModal ? "show" : ""}`}
        style={{
          display: showProductModal ? "block" : "none",
          backgroundColor: "rgba(0,0,0,0.5)",
        }}
        tabIndex="-1"
        aria-labelledby="addProductModalLabel"
        aria-hidden={!showProductModal}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h6
                className="modal-title text-primary"
                id="addProductModalLabel"
              >
                Add Product
              </h6>
              <button
                type="button"
                className="btn-close"
                style={{ fontSize: "10px" }}
                onClick={closeProductModal}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleProductSubmit}>
                <div className="row">
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label htmlFor="item_code" className="form-label">
                        Item Code
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="item_code"
                        value={productAddData.item_code}
                        disabled
                        readOnly
                      />
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label htmlFor="product_category" className="form-label">
                        Category <span className="text-danger">*</span>
                      </label>
                      <select
                        className={`form-select ${productFormErrors.category ? "is-invalid" : ""}`}
                        id="product_category"
                        value={productAddData.category}
                        onChange={(e) =>
                          setProductAddData({
                            ...productAddData,
                            category: e.target.value,
                          })
                        }
                      >
                        <option value="">Select Category</option>
                        <option value="Gold">Gold</option>
                        <option value="Silver">Silver</option>
                        <option value="Gold Coin">Gold Coin</option>
                        <option value="Silver Coin">Silver Coin</option>
                      </select>
                      {productFormErrors.category && (
                        <div className="invalid-feedback">
                          {productFormErrors.category}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-lg-12">
                    <div className="mb-3">
                      <label htmlFor="item_name_english" className="form-label">
                        Item Name <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <input
                          type="text"
                          className={`form-control ${productFormErrors.item_name ? "is-invalid" : ""}`}
                          id="item_name_english"
                          placeholder="Enter item name in English (Tamil will auto-translate)"
                          value={productAddData.item_name_english}
                          onChange={(e) =>
                            setProductAddData({
                              ...productAddData,
                              item_name_english: e.target.value,
                              item_name: e.target.value,
                              item_name_tamil: "",
                            })
                          }
                          onBlur={async (e) => {
                            const val = e.target.value.trim();
                            if (val && !productAddData.item_name_tamil) {
                              const tamil = await translateToTamil(val);
                              if (tamil) {
                                setProductAddData((prev) => ({
                                  ...prev,
                                  item_name_tamil: tamil,
                                }));
                                toast.info(
                                  `Tamil translation: ${tamil}`,
                                  2000,
                                  "topRight",
                                );
                              }
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          title="Translate to Tamil"
                          disabled={
                            isTranslating || !productAddData.item_name_english
                          }
                          style={{ height: "35px" }}
                          onClick={async () => {
                            const tamil = await translateToTamil(
                              productAddData.item_name_english,
                            );
                            if (tamil) {
                              setProductAddData((prev) => ({
                                ...prev,
                                item_name_tamil: tamil,
                              }));
                              toast.success(
                                "Translated successfully!",
                                1500,
                                "topRight",
                              );
                            } else {
                              toast.error(
                                "Translation failed. Please try again.",
                                2000,
                                "topRight",
                              );
                            }
                          }}
                        >
                          {isTranslating ? (
                            <span className="spinner-border spinner-border-sm" />
                          ) : (
                            "🌐 Translate"
                          )}
                        </button>
                      </div>
                      {productAddData.item_name_tamil && (
                        <small className="text-muted mt-1 d-block">
                          <strong>Tamil:</strong>{" "}
                          {productAddData.item_name_tamil}
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 ms-2 text-muted"
                            onClick={() =>
                              setProductAddData((prev) => ({
                                ...prev,
                                item_name_tamil: "",
                              }))
                            }
                          >
                            ✕ clear
                          </button>
                        </small>
                      )}
                      {productFormErrors.item_name && (
                        <div className="invalid-feedback">
                          {productFormErrors.item_name}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label htmlFor="pure_percent" className="form-label">
                        Purity (%) <span className="text-danger">*</span>
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        max="100"
                        className={`form-control ${productFormErrors.pure_percent ? "is-invalid" : ""}`}
                        id="pure_percent"
                        placeholder="e.g. 99.9"
                        value={productAddData.pure_percent}
                        onChange={(e) =>
                          setProductAddData({
                            ...productAddData,
                            pure_percent: e.target.value,
                          })
                        }
                      />
                      {productFormErrors.pure_percent && (
                        <div className="invalid-feedback">
                          {productFormErrors.pure_percent}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label htmlFor="product_status" className="form-label">
                        Status <span className="text-danger">*</span>
                      </label>
                      <select
                        className={`form-select ${productFormErrors.status ? "is-invalid" : ""}`}
                        id="product_status"
                        value={productAddData.status}
                        onChange={(e) =>
                          setProductAddData({
                            ...productAddData,
                            status: e.target.value,
                          })
                        }
                      >
                        <option value="">Select Status</option>
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </select>
                      {productFormErrors.status && (
                        <div className="invalid-feedback">
                          {productFormErrors.status}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="mb-3">
                    <label htmlFor="product_image" className="form-label">
                      Image Upload
                    </label>
                    <input
                      type="file"
                      className="form-control"
                      id="product_image"
                      onChange={(e) =>
                        setProductAddData({
                          ...productAddData,
                          image: e.target.files[0],
                        })
                      }
                      accept="image/*"
                    />
                  </div>
                </div>

                <div className="text-center mt-4">
                  <button
                    type="submit"
                    className="btn btn-primary btn-sm px-4"
                    disabled={loading}
                  >
                    {loading ? "Adding..." : "Add Product"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {(showModal || showCustomerModal || showProductModal) && (
        <div className="modal-backdrop fade show"></div>
      )}
    </>
  );
}

export default AddLoan;
